import * as d3 from "d3";
import { format } from "date-fns";
import { useLayoutEffect, useRef } from "react";

interface DateTimelineAxesPropsType<T> {
  chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale: d3.ScaleBand<Date>;
  yScale: d3.ScaleLinear<number, number>;
  data: T[];
  dateKey: keyof T;
  width: number;
  height: number;
  isXAxisVisible: boolean;
  isYAxisVisible: boolean;
  xAxisStyle: {
    color?: string;
    fontSize?: string | number;
  };
  yAxisStyle: {
    color?: string;
    fontSize?: string | number;
  };
}

export function DateTimelineAxes<T>({
  chartGroup: g,
  xScale,
  yScale,
  data,
  dateKey,
  width,
  height,
  isXAxisVisible,
  isYAxisVisible,
  xAxisStyle,
  yAxisStyle,
}: DateTimelineAxesPropsType<T>) {
  const xAxisRef = useRef<d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  > | null>(null);
  const yAxisRef = useRef<d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  > | null>(null);

  useLayoutEffect(() => {
    if (!g) return;

    // To avoid cluttering the axis, we'll only show a subset of ticks if there are too many.
    const maxTicks = Math.max(1, Math.floor(width / 80));
    const tickValues =
      data.length > maxTicks
        ? data
            .filter((_, i) => i % Math.ceil(data.length / maxTicks) === 0)
            .map((d) => d[dateKey] as Date)
        : data.map((d) => d[dateKey] as Date);

    // Create and configure the main x-axis.
    const xAxis = d3
      .axisBottom(xScale)
      .tickFormat((d) => format(d as Date, "MMM dd"))
      .tickValues(tickValues);

    const yAxis = d3.axisLeft(yScale);

    if (!xAxisRef.current) {
      xAxisRef.current = g.append("g").attr("class", "axis x-axis");
    }
    xAxisRef.current
      .attr("transform", `translate(0,${height})`)
      .interrupt()
      .call(xAxis)
      .style("color", xAxisStyle.color)
      .style("font-size", xAxisStyle.fontSize)
      .style("opacity", isXAxisVisible ? 1 : 0);

    if (!yAxisRef.current) {
      yAxisRef.current = g.append("g").attr("class", "axis y-axis");
    }
    yAxisRef.current
      .interrupt()
      .call(yAxis)
      .style("color", yAxisStyle.color)
      .style("font-size", yAxisStyle.fontSize)
      .style("opacity", isYAxisVisible ? 1 : 0);
  }, [
    g,
    xScale,
    yScale,
    data,
    dateKey,
    width,
    height,
    isXAxisVisible,
    isYAxisVisible,
    xAxisStyle,
    yAxisStyle,
  ]);

  return null;
}
